import { useCallback, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { Box, Menu, SearchIcon, Text, TextField } from '@citibox-panel/ui';
import { track } from '@citibox-panel/analytics';
import type { TrackingMatch, TrackingSearchResponse } from '@citibox-panel/api-client';
import { countBucket } from '../../../lib/countBucket';
import { HEADER_SEARCH_NOTICE_ID, SEARCH_FAILED } from '../../../lib/headerSearchNotice';
import { detectPhone } from '../../phone-search/lib/detectPhone';
import { useErrorNotice } from '../../search-shell/hooks/useErrorNotice';
import { useSearchTabs } from '../../search-shell/hooks/useSearchTabs';
import { useTrackingSearch } from '../hooks/useTrackingSearch';
import { detectTracking } from '../lib/detectTracking';
import { TrackingMatchesDialog } from './TrackingMatchesDialog';

export interface TrackingSearchProps {
  search: (tracking: string) => Promise<TrackingSearchResponse>;
  // The host focuses the header input on ⌘K / «+» / the empty-state action (shell seam).
  inputRef?: Ref<HTMLInputElement>;
  // Optional control rendered AS the field's end adornment — the host's type trigger. Named as in
  // the sibling modes, so the host hands the same node to whichever is live.
  trailing?: ReactNode;
}

// The wording of the two endings that have no place on screen. They are separate constants and not
// one parameterized string because the DIFFERENCE is the requirement: «not found» names the window
// on purpose — the modal's own count line rotulates «ÚLTIMOS 90 DÍAS», so an answer that omits it
// when there are no results lets the agent read «does not exist» where the panel means «not in the
// last 90 days». And while the endpoint is undelivered every real read is a 404, so the failure has
// to sound like a breakdown or the worst possible message is the one the agent sees all day.
// TODO: i18n
const NOTICE = {
  none: 'No hemos podido encontrar lo que buscas en los últimos 90 días',
  // Shared with the phone mode, which fails the same way and says the same thing: the id below is
  // the header SURFACE, so whichever mode is live owns the one notice (`lib/headerSearchNotice`).
  error: SEARCH_FAILED,
} as const;

export function TrackingSearch({ search, inputRef, trailing }: TrackingSearchProps) {
  const { openDeliveryInNewSearch, openDeliveryTab } = useSearchTabs();
  const { notify, dismiss } = useErrorNotice();
  const { submit, status } = useTrackingSearch(search);
  const [value, setValue] = useState('');
  // The rows the modal is showing, or null when it is closed. `count` rides along because it is the
  // window's total and the rows are capped — the modal needs both to say «truncated».
  const [matches, setMatches] = useState<{ rows: TrackingMatch[]; count: number } | null>(null);
  // A ref and not state, because nothing here may render on its own: the render that matters is the
  // one the dialog's exit triggers, and it has to land after MUI has removed the modal.
  const picked = useRef<TrackingMatch | null>(null);

  // The dropdown anchors to the input itself, so the node is forwarded both to the anchor ref and
  // to the caller's `inputRef` (which is what ⌘K focuses).
  const anchorRef = useRef<HTMLInputElement | null>(null);
  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      anchorRef.current = node;
      if (typeof inputRef === 'function') inputRef(node);
      else if (inputRef) inputRef.current = node;
    },
    [inputRef],
  );

  // Opens the delivery under its titular. The tab is keyed by the titular's PHONE because that is
  // the key the «Usuario» read resolves by, and the tab's own account read sends it back to the
  // endpoint.
  const openMatch = (match: TrackingMatch): void => {
    // Through the ONE judge of what a phone is, which already tolerates spaces, dashes and
    // parentheses and returns the E.164 form. Normalizing here instead would be a second
    // definition of a tab's key, and the key is what gets sent back to the endpoint.
    const phone = detectPhone(match.recipient?.phone ?? '');
    // No titular the panel can key a tab by — either there is nobody at all, or the account was
    // anonymized and its phone nulled. The delivery EXISTS and has nowhere to hang, so it gets a
    // tab of its own, labelled by the tracking: the only name it has. Reporting «no encontrado»
    // here would be the worst kind of wrong — a real parcel called non-existent because its
    // titular's account was closed.
    if (phone === null) {
      openDeliveryTab(match.id, match.tracking);
      return;
    }
    openDeliveryInNewSearch(
      phone,
      match.recipient?.name ?? undefined,
      match.id,
      match.tracking,
      'tracking',
    );
  };

  const runSearch = async (): Promise<void> => {
    // Cleared before asking, so a retry starts from a header with nothing on it.
    dismiss(HEADER_SEARCH_NOTICE_ID);
    const outcome = await submit(value);
    // Null is «nothing was asked» — the term is below the floor, which the hint already answers.
    // Nothing is reported either: an event here would count keystrokes as searches.
    if (outcome === null) return;
    // In the awaited handler and never in a render effect: a cache hit resolves with no
    // loading→resolved transition, so an effect would miss it and a re-render would double it.
    // Categorical dimensions ONLY — the tracking is an identifier and the titular's name and phone
    // are «Usuario» PII, so none of the three reaches telemetry. The bucket rides only on
    // `multiple`, which is the outcome where «how many» is the question worth watching, and it
    // comes from the shared helper so the dimension stays comparable with the other lists.
    track('tracking_search_submitted', {
      result: outcome.result,
      ...(outcome.result === 'multiple' ? { matches_bucket: countBucket(outcome.count) } : {}),
    });
    if (outcome.result === 'single') openMatch(outcome.match);
    else if (outcome.result === 'multiple') {
      setMatches({ rows: outcome.matches, count: outcome.count });
    } else notify(HEADER_SEARCH_NOTICE_ID, NOTICE[outcome.result]);
  };

  // REMEMBERS. Everything a pick causes happens on the way out, so this handler touches nothing
  // outside itself.
  const handlePick = (match: TrackingMatch): void => {
    picked.current = match;
    setMatches(null);
  };

  // ACTS, at the one moment MUI has removed the modal AND stripped `aria-hidden` off the page: the
  // tab created here mounts on a page assistive tech can read, instead of inside a subtree nobody
  // is listening to.
  const handleExited = (): void => {
    const match = picked.current;
    // A close that picked nothing — Escape, the backdrop, the ✕ — has nothing to replay.
    if (match === null) return;
    // Consumed, so a later dismissal cannot reopen the delivery the agent already left.
    picked.current = null;
    openMatch(match);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    void runSearch();
  };

  // A non-empty term that has not cleared the floor shows the hint and never searches (the judge
  // gates the submit), so the agent is told what is missing rather than left with a dead Enter.
  const belowFloor = value.trim().length > 0 && detectTracking(value) === null;
  const isSearching = status === 'searching';

  return (
    // `width` as well as the field's own `fullWidth`: as a flex item in the header's slot this box
    // shrinks to its content, so the field would stop short of the slot's edge. `relative` is what
    // lets the hint below hang under it without pushing the chrome down.
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        // Names the field for a screen reader and matches its visible placeholder; distinct from
        // the other modes' names so one query never matches two fields.
        // TODO: i18n
        aria-label="Buscar por tracking"
        placeholder="Buscar por tracking…"
        value={value}
        onChange={setValue}
        // Bound to the input and not to the control root: the host's type trigger renders inside
        // this field's end adornment, and activating it is not a way to submit what was typed.
        onKeyDown={onKeyDown}
        ref={setInputRef}
        variant="search"
        fullWidth
        startAdornment={<SearchIcon fontSize="small" />}
        {...(trailing !== undefined && trailing !== null ? { endAdornment: trailing } : {})}
      />
      {belowFloor && (
        // Absolute, so a short term cannot stretch the header: the hint floats over the content
        // below instead of pushing the chrome down. Its positioning parent is the wrapper above,
        // and the zero padding plus the negative margin cancel the caption's half-leading — the
        // same recipe as the phone hint, for the same reason.
        <Box sx={{ position: 'absolute', top: '100%', left: 0, px: 1.5, pt: 0, mt: -0.5 }}>
          {/* TODO: i18n */}
          <Text variant="caption" color="secondary">
            Introduce al menos 3 caracteres del número de tracking
          </Text>
        </Box>
      )}
      {/* Only ever the loading row: every ENDING of this search is elsewhere — one match opens a
          tab, several open the modal, and nothing found or a failed read float as a notice. So
          there is no candidate list here and no empty state either. */}
      <Menu
        anchorEl={anchorRef.current}
        open={isSearching}
        // Nothing to dismiss: the row's lifetime IS the read's, and a spinner the agent can close
        // while the request is still in flight would be a lie about what is happening.
        onClose={() => undefined}
      >
        <Box sx={{ px: 2, py: 1 }}>
          {/* TODO: i18n */}
          <Text variant="body2" color="secondary">
            Buscando…
          </Text>
        </Box>
      </Menu>
      <TrackingMatchesDialog
        open={matches !== null}
        matches={matches?.rows ?? []}
        count={matches?.count ?? 0}
        onClose={() => {
          setMatches(null);
        }}
        onExited={handleExited}
        onPick={handlePick}
      />
    </Box>
  );
}
